import { useCallback } from 'react';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { StandardActionModal } from 'src/common/components/standard-action-modal';
import { StyledModalIcon } from 'src/common/components/standard-action-modal/standard-action-modal.styles';
import { OptionsModalProps } from 'src/features/room/room.types';
import { useIsLockedInRoomQuery } from 'src/features/manage-account/teams/team.slice';
import { useLeaveRoomAsTeamMutation, useLeaveRoomMutation } from '../../room.slice';
const iconComponent = <StyledModalIcon icon={faCircleExclamation} data-icon="error" />;
const getLeaveModalBodyText = (isTeamsRoom: boolean, asTeam: boolean, isTeamLocked: boolean) => {
  if (!isTeamsRoom) {
    return 'If you choose to leave the room, your progress will still be in the room.';
  }
  if (isTeamLocked && asTeam) {
    return "The event in the room is active. If you leave as a team, your team's progress will be lost. Are you sure you want to proceed?";
  }
  if (isTeamLocked) {
    return "The event in the room is active. If you are the last member of your team to leave, your team's progress will be lost. Are you sure you want to proceed?";
  }
  return 'If you choose to leave the room, your team progress will still be in the room.';
};
export const LeaveRoomModal = ({
  open,
  roomCode,
  asTeam,
  isTeamsRoom,
  areChangesInTeamLocked,
  onOpenChange,
  onClosed
}: OptionsModalProps) => {
  const [leaveRoomTrigger, {
    isLoading
  }] = useLeaveRoomMutation();
  const [leaveRoomAsTeamTrigger, {
    isLoading: isLeavingAsTeamLoading
  }] = useLeaveRoomAsTeamMutation();
  const {
    data: lockStatusData
  } = useIsLockedInRoomQuery(roomCode, {
    skip: !open || !roomCode || !isTeamsRoom || !areChangesInTeamLocked
  });
  const isTeamLocked = !!lockStatusData?.data?.isLocked;
  const navigate = useNavigate();
  const handleModalAction = useCallback(async () => {
    try {
      const result = asTeam ? await leaveRoomAsTeamTrigger({
        roomCode
      }).unwrap() : await leaveRoomTrigger({
        roomCode
      }).unwrap();
      const hasUserLeftRoom = result.status === 'success';
      if (hasUserLeftRoom) {
        navigate('/hacktivities?tab=search');
      }
    } catch (error) {
      const {
        data: responseData
      } = (error as {
        data?: {
          message?: string;
        };
      });
      NotificationDispatch('error', responseData?.message || 'Something went wrong. Please try again.');
    } finally {
      onClosed?.();
    }
  }, [leaveRoomTrigger, leaveRoomAsTeamTrigger, onClosed, navigate, roomCode, asTeam]);
  return <StandardActionModal modalTitleText="Leave Room" modalBodyText={getLeaveModalBodyText(!!isTeamsRoom, !!asTeam, isTeamLocked)} actionButtonTitle="Leave Room" dismissButtonTitle="Cancel" handleModalAction={handleModalAction} showIcon iconComponent={iconComponent} open={open} onOpenChange={onOpenChange} actionLoading={isLoading || isLeavingAsTeamLoading} data-sentry-element="StandardActionModal" data-sentry-component="LeaveRoomModal" data-sentry-source-file="leave-room-modal.tsx" />;
};